import React, { useEffect, useMemo, useState } from 'react';
import { Select, Empty, Segmented, Pagination, Spin, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PartitionOutlined } from '@ant-design/icons';
import { PageHeader } from 'components/headings';
import SectionHint from 'components/ciclo/SectionHint';
import CicloRow from 'components/ciclo/CicloRow';
import { useCicloTablero, useCicloEtapa } from 'hooks/useApi/useCicloApi';
import { CICLO_ETAPAS, CICLO_ETAPA_UI } from 'assets/data/cicloMeta';
import { useClientes } from 'providers/Clientes/ClientesProvider';
import { useTrazabilidadDrawer } from 'providers/Trazabilidad/TrazabilidadDrawerProvider';
import paths from 'assets/routes/paths';

const { useToken } = theme;
const PAGE_SIZE = 25;
const SEVERIDADES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'].map((s) => ({ value: s, label: s }));

// Navegación de la acción de próximo paso (reusa los flujos existentes).
const ACCION_NAV = {
  presupuestar: `${paths.admin.hallazgos}?etapa=DETECTADO`,
  generar_odt: `${paths.admin.budget.root}?stage=aprobado_sin_odt`,
  asignar_odt: `${paths.admin.budget.root}?stage=odt_no_asignada,odt_asignada,odt_en_proceso`,
};

/**
 * Cola accionable del ciclo (`/admin/ciclo`): selector de etapas con sus
 * conteos + lista densa paginada de la etapa activa. Reemplaza el kanban de
 * 5 columnas: el ejecutivo elige una etapa y trabaja su cola fila por fila,
 * cada una con su próximo paso. Consume GET /kpi/ciclo (tablero para los
 * conteos de las pestañas, etapa para la cola paginada).
 */
function CicloPage() {
  const { token } = useToken();
  const navigate = useNavigate();
  const { call, data: clientesData, hasCalled } = useClientes();
  const { openHallazgo, openBudget, openOrder } = useTrazabilidadDrawer();

  const [etapa, setEtapa] = useState('DETECTADO');
  const [idClient, setIdClient] = useState(undefined);
  const [severidad, setSeveridad] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!hasCalled) call();
  }, [call, hasCalled]);

  useEffect(() => {
    setPage(1);
  }, [etapa, idClient, severidad]);

  const clientes = useMemo(() => {
    const raw = clientesData?.data || clientesData || [];
    if (!Array.isArray(raw)) return [];
    return raw.filter((c) => c && c.id && c.name).map((c) => ({ value: c.id, label: c.name }));
  }, [clientesData]);

  // Conteos de las pestañas: reusa el shape { columnas } (no rompe la Home).
  const { columnas } = useCicloTablero({ idClient, severidad, limit: 1 });
  const countByEtapa = useMemo(() => {
    const m = {};
    (columnas || []).forEach((c) => {
      m[c.etapa] = c.count;
    });
    return m;
  }, [columnas]);

  const { data: cola, loading } = useCicloEtapa({
    etapa,
    idClient,
    severidad,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const rows = cola?.rows ?? [];
  const total = cola?.count ?? 0;

  const ballDot = (key) => {
    const ball = CICLO_ETAPA_UI[key]?.ball;
    return ball === 'tuya' || ball === 'condicional'
      ? token.colorPrimary
      : token.colorTextQuaternary;
  };

  const segmentedOptions = CICLO_ETAPAS.map((e) => ({
    value: e.key,
    label: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: ballDot(e.key) }} />
        {e.label}
        <span style={{ fontVariantNumeric: 'tabular-nums', color: token.colorTextTertiary }}>
          {countByEtapa[e.key] ?? '—'}
        </span>
      </span>
    ),
  }));

  const onAccion = () => {
    const nav = ACCION_NAV[CICLO_ETAPA_UI[etapa]?.accion];
    if (nav) navigate(nav);
  };
  const onVerDetalle = (row) => {
    if (row.kind === 'hallazgo') openHallazgo(row.id);
    else if (row.orderId) openOrder(row.orderId);
    else openBudget(row.id);
  };
  const onEtapaChange = (value) => {
    setEtapa(value);
    if (value !== 'DETECTADO') setSeveridad([]);
  };

  const hintActual = CICLO_ETAPAS.find((e) => e.key === etapa)?.hint;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingTop: token.paddingMD,
        paddingBottom: token.paddingXL,
      }}
    >
      <PageHeader
        icon={PartitionOutlined}
        title="Ciclo de trabajo"
        subtitle="Tu cola de trabajo, etapa por etapa. Cada fila lleva su próximo paso."
      />
      <SectionHint
        id="ciclo-cola"
        message="Elegí una etapa y trabajá su cola"
        description="Las etapas con punto azul son las que dependen de vos; las demás son seguimiento. La lista viene priorizada por lo que más urge."
      />

      <Segmented value={etapa} onChange={onEtapaChange} options={segmentedOptions} size="large" />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Cliente"
          style={{ minWidth: 220 }}
          value={idClient}
          onChange={setIdClient}
          options={clientes}
        />
        {etapa === 'DETECTADO' ? (
          <Select
            mode="multiple"
            allowClear
            placeholder="Severidad"
            style={{ minWidth: 220 }}
            value={severidad}
            onChange={setSeveridad}
            options={SEVERIDADES}
            maxTagCount="responsive"
          />
        ) : null}
        <span style={{ fontSize: 12.5, color: token.colorTextTertiary }}>{hintActual}</span>
      </div>

      <div
        style={{
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          overflow: 'hidden',
          minHeight: 120,
        }}
      >
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : rows.length ? (
          rows.map((row) => (
            <CicloRow
              key={`${row.kind}-${row.id}`}
              row={row}
              etapa={etapa}
              onAccion={onAccion}
              onVerDetalle={onVerDetalle}
            />
          ))
        ) : (
          <div style={{ padding: 40 }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay items en esta etapa" />
          </div>
        )}
      </div>

      {total > PAGE_SIZE ? (
        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={total}
          showSizeChanger={false}
          onChange={setPage}
          style={{ alignSelf: 'flex-end' }}
        />
      ) : null}
    </div>
  );
}

export default CicloPage;
